import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest, Subject } from 'rxjs';
import { filter, startWith, takeUntil } from 'rxjs/operators';
import { SharedService } from 'src/app/core/service/shared.service';
import { SessionService } from 'src/app/core/service/session.service';
import { EntitlementsService } from 'src/app/core/service/entitlements.service';
import { NavGroup, NavItem, NavRow } from 'src/app/core/model/nav-item';
import {
  buildNavGroups, buildSkeletonRows, NAV_ITEMS, navIconPath, navIconSrc
} from 'src/app/core/config/nav-items.config';
import { ManageIntercom } from 'src/app/core/util/manage-intercom';
import { AuthService } from '../../core/service/auth/auth.service';
import { PracticeMe, PracticeService } from 'src/app/data/service/practice.service';

/**
 * SD-1218: the sidemenu, rendered from configuration.
 *
 * Previously this was 302 lines of hand-written markup in which every item repeated the same
 * five concerns (two icon variants, a tooltip, a routerLink, a role compare) and the whole
 * menu appeared twice -- once as the live list and once as a loading skeleton that had to be
 * kept in step by hand. Both now come from NAV_ITEMS through buildNavGroups/buildSkeletonRows,
 * so they cannot drift.
 *
 * The menu has two shapes, and the same groups drive both:
 *
 *   * Expanded: a labelled group per section, which opens and closes, with its children under
 *     it. The group holding the current page opens on arrival.
 *   * Collapsed: one icon per group. Clicking one goes to that group's first page and leaves
 *     the group open, so expanding afterwards lands where you already are.
 */
@Component({
  selector: 'app-sidebar-menu',
  templateUrl: './sidebar-menu.component.html',
  styleUrls: ['./sidebar-menu.component.scss']
})
export class SidebarMenuComponent implements OnInit, OnDestroy {

  /** The skeleton shape, derived from the same config as the live menu. */
  readonly skeletonRows: NavRow[] = buildSkeletonRows();

  /** True until both the role and the entitlement set have arrived. */
  isLoading = true;

  /** Held rather than piped through async: the component reads them too, to open the active group. */
  topGroups: NavGroup[] = [];
  bottomGroups: NavGroup[] = [];

  supportOpen = false;

  /** SD-1248: beside "All clients", the clients across the user's practices that need action. */
  practiceBadge = 0;

  /** The URL the menu is highlighting against, without its query string. */
  currentUrl = '';

  /**
   * Groups the reader has closed, by section id. Everything is open until then -- the menu is
   * the map of the product, and a page nobody can see is a page nobody visits. Arriving at a
   * page reopens its group.
   */
  private closedGroups: { [sectionId: string]: boolean } = {};

  private _unsubscribe$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private router: Router,
    public sharedService: SharedService,
    private session: SessionService,
    private entitlements: EntitlementsService,
    private practice: PracticeService
  ) { }

  /** Expanded is the shared state the header's toggle writes to as well. */
  get expanded(): boolean {
    return !!this.sharedService.isShowSideNav;
  }

  ngOnInit() {
    // Open by default, which is the menu with its labels. Not on a narrow screen, where the
    // expanded menu would take a third of the width from the page it is pointing at.
    this.sharedService.isShowSideNav =
      typeof window === 'undefined' || window.innerWidth >= 992;

    this.currentUrl = this.cleanUrl(this.router.url);
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntil(this._unsubscribe$))
      .subscribe((e: NavigationEnd) => {
        this.currentUrl = this.cleanUrl(e.urlAfterRedirects || e.url);
        this.openActiveGroup();
      });

    // One shared source for both halves of the menu. SessionService and EntitlementsService
    // are each shareReplay(1), so this costs one request between them however many
    // components subscribe -- where previously the role alone was fetched 31 times.
    // state$ rather than modules$: the rows need `fromFallback` as well as the codes, because
    // the client-side role gate may only be applied to a LEGACY_FALLBACK set. A server-resolved
    // set has already had dbo.Module.AllowedRoleMask applied to it, and re-filtering it here
    // would silently override whatever an admin configured in the Modules screen.
    // SD-1248: practice/me decides the "All clients" item. It starts empty rather than holding
    // the menu back: the item is the exception, and it appears when the answer does.
    const resolved$ = combineLatest([
      this.session.role$,
      this.entitlements.state$,
      this.practice.me$.pipe(startWith({ practices: [] } as PracticeMe))
    ]).pipe(takeUntil(this._unsubscribe$));

    resolved$.subscribe(
      ([role, state, me]) => {
        const hasPractice = me.practices.length > 0;
        this.practiceBadge = me.practices.reduce((n, p) => n + (p.needsActionCount || 0), 0);
        this.topGroups = buildNavGroups(role, state.modules, 'top', NAV_ITEMS, state.fromFallback, hasPractice);
        this.bottomGroups = buildNavGroups(role, state.modules, 'bottom', NAV_ITEMS, state.fromFallback, hasPractice);
        this.isLoading = false;
        this.openActiveGroup();
      },
      () => {
        // EntitlementsService already falls back rather than erroring, so this is only
        // reached if the role stream itself dies. Showing the skeleton forever would be
        // worse than showing whatever the fallback produced.
        this.isLoading = false;
      }
    );

    window.addEventListener('intercom:closed', this.onIntercomClosed);
  }

  ngOnDestroy() {
    window.removeEventListener('intercom:closed', this.onIntercomClosed);
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  toggleSidebar() {
    this.sharedService.isShowSideNav = !this.sharedService.isShowSideNav;
  }

  // ── Active state ─────────────────────────────────────────────────────────────────────────
  // Worked out from the URL rather than routerLinkActive, because a group has to know whether
  // any of its children is the current page, and a directive on the child cannot tell it.

  isItemActive(item: NavItem): boolean {
    const path = item && item.route ? item.route.join('/') : null;
    if (!path) { return false; }
    if (item.exact) { return this.currentUrl === path; }
    return this.currentUrl === path || this.currentUrl.indexOf(path + '/') === 0;
  }

  isGroupActive(group: NavGroup): boolean {
    return (group.items || []).some(item => this.isItemActive(item));
  }

  isGroupOpen(group: NavGroup): boolean {
    if (!group.section) { return false; }
    return !this.closedGroups[group.section.id];
  }

  toggleGroup(group: NavGroup): void {
    if (!group.section) { return; }
    this.closedGroups[group.section.id] = this.isGroupOpen(group);
  }

  /** Open whichever group holds the current page, so arriving anywhere reveals where you are. */
  private openActiveGroup(): void {
    this.topGroups.forEach(group => {
      if (group.section && this.isGroupActive(group)) {
        this.closedGroups[group.section.id] = false;
      }
    });
  }

  private cleanUrl(url: string): string {
    return (url || '').split('?')[0].split('#')[0];
  }

  /** The count drawn beside an item, or 0 for none. */
  badgeFor(item: NavItem): number {
    return item && item.requires === 'practice' ? this.practiceBadge : 0;
  }

  // ── Icons ────────────────────────────────────────────────────────────────────────────────

  iconPath(item: NavItem, isActive: boolean): string {
    return navIconPath(item, isActive);
  }

  groupIcon(group: NavGroup, isActive: boolean): string {
    return navIconSrc(group.section ? group.section.icon : group.items[0].icon, isActive);
  }

  trackGroup(index: number, group: NavGroup): string {
    return group.section ? 'section:' + group.section.id : 'item:' + group.items[0].requiredModule;
  }

  trackItem(index: number, item: NavItem): string {
    return item.requiredModule;
  }

  /** trackBy for the skeleton rows. */
  trackRow(index: number, row: NavRow): string {
    if (row.kind === 'item') { return 'item:' + row.item.requiredModule; }
    if (row.kind === 'section') { return 'section:' + row.label; }
    return 'placeholder:' + index;
  }

  // ── Clicks ───────────────────────────────────────────────────────────────────────────────

  onItemClick(event: MouseEvent, item: NavItem) {
    if (item.action === 'support') {
      this.toggleSupport();
      return;
    }

    const path = item.route ? item.route.join('/') : null;

    // Ctrl/Cmd-click opens in a new tab. Preserved from the previous onNavClick.
    if (path && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      window.open(path, '_blank');
      return;
    }

    if (item.clickHandler === 'payable') {
      this.resetPayableSessionState();
    }

    // Below 992px the menu slides over the page rather than sitting beside it, so going
    // somewhere has to get it out of the way. On a wide screen it stays put.
    if (typeof window !== 'undefined' && window.innerWidth < 992) {
      this.sharedService.isShowSideNav = false;
    }
  }

  /** A group's row: go to its first page, and make sure the group is open behind you. */
  onGroupClick(event: MouseEvent, group: NavGroup) {
    this.onGroupIconClick(event, group);
  }

  /**
   * A group's icon in the collapsed rail: go to its first page, and remember the group as open
   * so expanding afterwards shows where you landed rather than a closed group.
   */
  onGroupIconClick(event: MouseEvent, group: NavGroup) {
    if (group.section) {
      this.closedGroups[group.section.id] = false;
    }
    const first = (group.items || [])[0];
    if (first) {
      this.onItemClick(event, first);
    }
  }

  signOut() {
    // Subscribing is what sends the logout request; without it the server
    // session stayed alive and the next visit signed straight back in.
    this.authService.signOut().subscribe({ error: () => { } });
    this.router.navigate(['auth/login'], { skipLocationChange: true });
  }

  toggleSupport() {
    this.supportOpen = !this.supportOpen;
    if (this.supportOpen) {
      ManageIntercom.show();
    } else {
      ManageIntercom.hide();
    }
  }

  onIntercomClosed = () => {
    this.supportOpen = false;
  }

  /**
   * Clears the payable screens' sessionStorage scratch state so they open fresh. Carried over
   * verbatim from the previous RedirectToPayable/RedirectToReport, which set the same four
   * keys.
   */
  private resetPayableSessionState() {
    sessionStorage.IsExecutedPayableReport = false;
    sessionStorage.IsFromPaymentTab = false;
    sessionStorage.start = '';
    sessionStorage.SelectedPayableReportId = '';
  }
}
